"use client";

import { useRef, type ReactNode } from "react";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { blurReveal, dotToPill, fadeUp, reduceMotion } from "@/lib/motion";
import { TextReveal } from "@/components/motion/TextReveal";
import { Eyebrow } from "@/components/ui/Eyebrow";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

type SectionIntroProps = {
  eyebrow: string;
  eyebrowIcon?: ReactNode;
  /** Sits to the left of the eyebrow and fades up with it — a back control, usually. */
  beforeEyebrow?: ReactNode;
  title: string;
  titleId?: string;
  text?: string;
  cta?: { label: string; href: string };
  /** Overrides the CTA colour; defaults to the tone-appropriate dark/light. */
  ctaVariant?: "dark" | "light" | "mint";
  /** A secondary action set beside the CTA — it wraps under it in a narrow column. */
  ctaAside?: ReactNode;
  align?: "center" | "left";
  tone?: "light" | "dark";
  as?: "h1" | "h2" | "h3";
  size?: "section" | "split";
  className?: string;
  titleClassName?: string;
  /** Classes for the breaks a "
" in `title` produces, e.g. "max-sm:hidden". */
  titleBreakClassName?: string;
  textClassName?: string;
  start?: string;
};

const sizes = {
  // Centered section statements (~42px at 1440)
  section: "text-[clamp(2rem,2.95vw,2.9rem)] leading-[1.16] tracking-[-0.04em]",
  // Left-column headings in split layouts (~32–40px at 1440)
  split: "text-[clamp(1.75rem,2.35vw,2.3rem)] leading-[1.24] tracking-[-0.035em]",
};

/**
 * Eyebrow → heading → supporting copy → CTA, played as one choreographed timeline so
 * every section opens with the same cadence: label lands, words de-blur, copy settles,
 * the CTA grows out of a dot.
 */
export function SectionIntro({
  eyebrow,
  eyebrowIcon,
  beforeEyebrow,
  title,
  titleId,
  text,
  cta,
  ctaVariant,
  ctaAside,
  align = "center",
  tone = "light",
  as = "h2",
  size = "section",
  className,
  titleClassName,
  titleBreakClassName,
  textClassName,
  start = "top 80%",
}: SectionIntroProps) {
  const root = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = root.current;
      if (!el || reduceMotion()) return;
      const words = gsap.utils.toArray<HTMLElement>("[data-tr-unit]", el);
      const eyebrowEl = el.querySelector("[data-intro-eyebrow]");
      const textEl = el.querySelector("[data-intro-text]");
      const ctaEl = el.querySelector<HTMLElement>("[data-dot]");

      const tl = gsap.timeline({ scrollTrigger: { trigger: el, start, toggleActions: PLAY_ONCE } });
      tl.add(fadeUp(eyebrowEl, { duration: 0.8 }), 0).add(blurReveal(words, { stagger: 0.07 }), 0.12);
      if (textEl) tl.add(fadeUp(textEl, { duration: 1 }), 0.45);
      if (ctaEl) dotToPill(ctaEl, tl, 0.65);
    },
    { scope: root },
  );

  return (
    <div
      ref={root}
      className={cn("flex flex-col", align === "center" ? "items-center text-center" : "items-start text-left", className)}
    >
      <div
        data-intro-eyebrow=""
        data-reveal-init=""
        className={cn(beforeEyebrow && "flex flex-wrap items-center gap-x-3 gap-y-2")}
      >
        {beforeEyebrow}
        <Eyebrow tone={tone} icon={eyebrowIcon}>
          {eyebrow}
        </Eyebrow>
      </div>
      <TextReveal
        as={as}
        id={titleId}
        mode="manual"
        text={title}
        breakClassName={titleBreakClassName}
        className={cn(
          "mt-4 font-[450]",
          sizes[size],
          tone === "dark" ? "text-white" : "text-ink",
          align === "center" && "max-w-[14.2em]",
          titleClassName,
        )}
      />
      {text && (
        <p
          data-intro-text=""
          data-reveal-init=""
          className={cn(
            "mt-4 max-w-[42ch] text-[14px] leading-[1.6]",
            tone === "dark" ? "text-white/55" : "text-muted",
            textClassName,
          )}
        >
          {text}
        </p>
      )}
      {cta && (
        <div
          className={cn(
            "mt-7 flex flex-wrap items-center gap-x-5 gap-y-3",
            align === "center" ? "justify-center" : "justify-start",
          )}
        >
          {/* Mint is the primary action everywhere; it carries on the white page and the dark panels alike. */}
          <Button dot href={cta.href} variant={ctaVariant ?? "mint"}>
            {cta.label}
          </Button>
          {ctaAside}
        </div>
      )}
    </div>
  );
}
